import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import qs from 'qs';

export type StandardEnvironment = 'development' | 'standalone';
export type Environment = StandardEnvironment | 'oneweb';

export const deeplinkPrefixes = ['oabr', 'oab', 'oar', 'utm', ''];

export const marketingParamsDef = ['source', 'medium', 'campaign', 'term', 'content'] as const;
export type MarketingParam = typeof marketingParamsDef[number];
export const marketingParams = [...marketingParamsDef];

export const configParamsDef = [
  'token',
  'dealer',
  'decline',
  'offerId',
  'section',
  'dealerId',
  'bookingId',
  'vehicleId',
  'serviceNotes',
  'reservationId',
  'registrationId',
  'serviceProduct',
  'preferredDealerId',
] as const;
export type ConfigParam = typeof configParamsDef[number];
export const configParams = [...configParamsDef];

export const generalContextParamsDef = ['dealerGroup'] as const;
type GeneralContextParam = typeof generalContextParamsDef[number];
export const generalContextParams = [...generalContextParamsDef];

export const initializationParamsDef = ['context', 'locale'] as const;
type InitializationParam = typeof initializationParamsDef[number];

export const customizationParamsDef = ['dealer', 'dealerId'] as const;
export type CustomizationParam = typeof customizationParamsDef[number];

export const validParams = [
  ...configParamsDef,
  ...marketingParamsDef,
  ...generalContextParamsDef,
  ...initializationParamsDef,
  ...customizationParamsDef,
] as const;
export type ValidParam = ConfigParam | MarketingParam | GeneralContextParam | InitializationParam | CustomizationParam;

export type Params = { [key in ValidParam]?: string };

export const hasValidParam =
  (params: Params) =>
  (key: ValidParam): boolean =>
    params && validParams.includes(key);

export const someParamInParamsList = (params: Params, list: ValidParam[]): boolean => {
  const keys = Object.keys(params) as ValidParam[];
  return keys.some((param: ValidParam) => list.includes(param));
};

export const hasConfigParams = (params: Params): boolean => someParamInParamsList(params, configParams);

// * This next functions are only used for cleanQueryParams
// * which cleans the URL query

export const filterPrefixed = (rawParams: qs.ParsedQs): string => {
  const prefixesRegex = new RegExp(`^(${deeplinkPrefixes.join('|')})`);
  const onlyPrefixedKeys = Object.keys(rawParams).filter((key) => prefixesRegex.test(key));
  return qs.stringify(rawParams, { filter: onlyPrefixedKeys });
};

export const removePrefixes = (filteredParamsString: string): qs.ParsedQs => {
  const prefixesRegex = new RegExp(`^(${deeplinkPrefixes.join('|')})`, 'g');
  const parsed = qs.parse(filteredParamsString, { ignoreQueryPrefix: true });

  return Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key.replace(prefixesRegex, ''), value]));
};

// * Remove if a parameter is used in the query with both prefixes
// * eg 'oabrDealerId' and 'oabDealerId' or 'oabDealer' twice

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeDuplicates = (unprefixedParams: qs.ParsedQs): any => {
  let uniqueParams = {};
  Object.entries(unprefixedParams).forEach((pair) => {
    const lowerCaseParam = camelCase(pair[0]);
    uniqueParams = {
      ...uniqueParams,
      [lowerCaseParam]: Array.isArray(pair[1]) ? pair[1][0] : pair[1],
    };
  });
  return uniqueParams;
};

export const filterValidParams = (rawParams: qs.ParsedQs): Params => {
  let filteredParams = {};
  const keys = Object.keys(rawParams) as ValidParam[];
  keys.forEach((param: ValidParam) => {
    if (validParams.includes(param) && rawParams[param] !== '') {
      filteredParams = {
        ...filteredParams,
        [param]: rawParams[param],
      };
    }
  });
  return filteredParams;
};

export const cleanQueryParams = (rawParams: qs.ParsedQs): Params => {
  return filterValidParams(removeDuplicates(removePrefixes(filterPrefixed(removeDuplicates(rawParams)))));
};

export const filterConfigParams = (params: qs.ParsedQs): qs.ParsedQs => {
  const stringified = qs.stringify(params, { filter: configParams });
  return qs.parse(stringified, { ignoreQueryPrefix: true });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRawParams = (): any => {
  // We need to grab the hash query's and also search query's
  // and combine them with hash having precedence over search
  const hash = window?.top?.location?.hash?.split('?')[1];
  const hashAppendix = hash ? `&${hash}` : '';
  const queryAndHash = `${window?.top?.location?.search}${hashAppendix}`;
  // with both query's we clean them by removing all duplicates and just
  // keeping the whitelisted ones
  return qs.parse(queryAndHash, { ignoreQueryPrefix: true });
};

export const getParams = (): Params => {
  return cleanQueryParams(getRawParams());
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const objectKeysToKebab = (obj: Record<string, any>): Record<string, any> =>
  Object.keys(obj).reduce((result, key) => ({ [kebabCase(key)]: obj[key], ...result }), {});
